import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';

/**
 * One card on the entry page: icon + small uppercase title, an optional action
 * (buttons) on the right, then the content. Every section uses it so the page
 * reads as a set of equal blocks.
 */
export function DetailSection({
  icon: Icon,
  title,
  action,
  children,
  className,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn('p-4 sm:p-5', className)}>
      <div className="mb-3 flex min-h-8 items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Icon className="h-4 w-4 text-primary" /> {title}
        </h2>
        {action && <div className="flex shrink-0 flex-wrap gap-1.5">{action}</div>}
      </div>
      {children}
    </Card>
  );
}

/** A label above a value. */
export function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm font-medium">{children ?? '—'}</dd>
    </div>
  );
}

/** A small muted heading inside a section, with a divider above it. */
export function SubHeading({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 mt-4 border-t border-border/60 pt-3 text-xs font-medium text-muted-foreground">
      {children}
    </div>
  );
}
